/**
 * Batch detail — own tab (/instructor/batches/:batchId, /admin/batches/:batchId).
 * Students tab: the learners in the batch (add from the learner list, remove, assign extra courses).
 * Courses tab: the courses the batch follows (assigning one enrols every member).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel, useTabs } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { paneForm } from '../../components/shell/SidePane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { useAssignCoursesPane, CourseChecklist } from '../../components/batches/AssignCoursesPane';
import { BatchFormFields, batchBasePath } from './Batches';
import { toast } from 'sonner';
import { UsersThree, UserPlus, Books, Trash, PencilSimple, BookOpen, Student, ChartBar, MagnifyingGlass, Eye } from '@phosphor-icons/react';

export default function BatchDetail() {
  const { batchId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  const isAdmin = user?.role === 'tenant_admin';
  const base = batchBasePath(user?.role);

  const [batch, setBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('students');
  useTabLabel(batch?.name ? `Batch: ${batch.name}` : 'Batch');

  // panes
  const [pane, setPane] = useState(null); // 'add-students' | 'assign-courses' | 'edit'
  const [learners, setLearners] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [pick, setPick] = useState(new Set());
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const assign = useAssignCoursesPane({ api, onDone: () => load() });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get(`/batches/${batchId}`), api.get(`/batches/${batchId}/students`)]);
      setBatch(b.data);
      setStudents(s.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load batch');
    } finally {
      setLoading(false);
    }
  }, [api, batchId]);
  useEffect(() => { load(); }, [load]);

  const closePane = () => { setPane(null); setPick(new Set()); setQuery(''); assign.close(); };
  const openPane = async (which) => {
    closePane();
    if (which === 'add-students') {
      try { const r = await api.get('/users?role=learner'); setLearners(r.data || []); } catch { setLearners([]); }
    } else if (which === 'assign-courses') {
      try { const r = await api.get('/courses'); setAllCourses(r.data || []); } catch { setAllCourses([]); }
    } else if (which === 'edit') {
      setForm({ name: batch.name || '', code: batch.code || '', description: batch.description || '', instructor_id: batch.instructor_id || '', start_date: batch.start_date || '', end_date: batch.end_date || '', status: batch.status || 'active' });
      if (isAdmin) { try { const ins = await api.get('/instructors'); setInstructors(ins.data || []); } catch { setInstructors([]); } }
    }
    setPane(which);
  };
  const togglePick = (id) => setPick((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const addStudents = async (e) => {
    e.preventDefault();
    if (pick.size === 0) { toast.error('Select at least one learner'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/batches/${batchId}/students`, { ids: [...pick] });
      toast.success(`${res.data.added} student${res.data.added === 1 ? '' : 's'} added${res.data.enrolments_created ? ` · ${res.data.enrolments_created} course enrolments created` : ''}`);
      closePane(); load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add students'); } finally { setSaving(false); }
  };
  const removeStudent = async (s) => {
    if (!window.confirm(`Remove ${s.first_name} ${s.last_name} from this batch? Their course enrolments are kept.`)) return;
    try { await api.delete(`/batches/${batchId}/students/${s.id}`); toast.success('Removed from batch'); load(); } catch (error) { toast.error('Failed to remove'); }
  };
  const assignCourses = async (e) => {
    e.preventDefault();
    if (pick.size === 0) { toast.error('Select at least one course'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/batches/${batchId}/courses`, { ids: [...pick] });
      toast.success(`${res.data.added} course${res.data.added === 1 ? '' : 's'} assigned · ${res.data.enrolments_created} enrolments created`);
      closePane(); load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign courses'); } finally { setSaving(false); }
  };
  const unassignCourse = async (c) => {
    if (!window.confirm(`Remove "${c.title}" from this batch? Existing enrolments are kept.`)) return;
    try { await api.delete(`/batches/${batchId}/courses/${c.id}`); toast.success('Course removed from batch'); load(); } catch (error) { toast.error('Failed to remove course'); }
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/batches/${batchId}`, { ...form, code: form.code || null, instructor_id: form.instructor_id || null, start_date: form.start_date || null, end_date: form.end_date || null });
      toast.success('Batch updated'); closePane(); load();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };
  const deleteBatch = async () => {
    if (!window.confirm(`Delete batch "${batch?.name}"? Students keep their course enrolments.`)) return;
    try { await api.delete(`/batches/${batchId}`); toast.success('Batch deleted'); closeAndNavigate(`batch:view:${batchId}`, base); } catch (error) { toast.error('Failed to delete'); }
  };

  const memberIds = useMemo(() => new Set(students.map((s) => s.id)), [students]);
  const courseIds = useMemo(() => new Set((batch?.course_ids) || []), [batch]);

  let side = assign.side;
  if (pane === 'add-students') {
    const q = query.trim().toLowerCase();
    const candidates = learners.filter((l) => !memberIds.has(l.id)).filter((l) => !q || `${l.first_name} ${l.last_name} ${l.email || ''} ${l.phone || ''}`.toLowerCase().includes(q));
    const f = paneForm({ id: 'add-students-form', onSubmit: addStudents, onCancel: closePane, submitLabel: `Add ${pick.size || ''} student${pick.size === 1 ? '' : 's'}`.replace('  ', ' '), submitting: saving, submitIcon: UserPlus, submitDisabled: pick.size === 0,
      children: (
        <>
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search learners by name, email or phone" className="w-full h-8 pl-8 pr-2 rounded border border-input text-[13px] outline-none focus:border-shell-primary" data-testid="learner-search" />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{candidates.length} learner{candidates.length === 1 ? '' : 's'} not in this batch</span>
            {candidates.length > 0 && <button type="button" className="underline hover:text-foreground" onClick={() => setPick(new Set(candidates.map((c) => c.id)))}>Select all shown</button>}
          </div>
          <div className="max-h-[50vh] overflow-y-auto rounded border border-border divide-y" data-testid="learner-checklist">
            {candidates.length === 0 && <p className="p-3 text-xs text-muted-foreground">Everyone matching is already in the batch.</p>}
            {candidates.map((l) => (
              <label key={l.id} className="flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] hover:bg-muted/30 cursor-pointer">
                <Checkbox checked={pick.has(l.id)} onCheckedChange={() => togglePick(l.id)} data-testid={`learner-check-${l.id}`} />
                <span className="flex-1 min-w-0 truncate">{l.first_name} {l.last_name}<span className="text-muted-foreground"> · {l.email || l.phone}</span></span>
                {l.student_level && <span className="text-[11px] capitalize text-muted-foreground">{l.student_level}</span>}
              </label>
            ))}
          </div>
          {courseIds.size > 0 && <p className="field-hint">New members are enrolled in the batch's {courseIds.size} course{courseIds.size === 1 ? '' : 's'} automatically.</p>}
        </>
      ) });
    side = { open: true, onOpenChange: closePane, title: 'Add students', subtitle: batch?.name, icon: UserPlus, children: f.body, footer: f.footer, testid: 'add-students-pane', storageKey: 'pane-add-students' };
  } else if (pane === 'assign-courses') {
    const f = paneForm({ id: 'batch-courses-form', onSubmit: assignCourses, onCancel: closePane, submitLabel: `Assign ${pick.size || ''} course${pick.size === 1 ? '' : 's'}`.replace('  ', ' '), submitting: saving, submitIcon: BookOpen, submitDisabled: pick.size === 0,
      children: (
        <>
          <CourseChecklist courses={allCourses} selected={pick} onToggle={togglePick} locked={courseIds} query={query} onQuery={setQuery} />
          <p className="field-hint">Every student in the batch ({students.length}) is enrolled in each assigned course; students added later are enrolled automatically.</p>
        </>
      ) });
    side = { open: true, onOpenChange: closePane, title: 'Assign courses to batch', subtitle: batch?.name, icon: Books, children: f.body, footer: f.footer, testid: 'batch-courses-pane', storageKey: 'pane-batch-courses' };
  } else if (pane === 'edit' && form) {
    const f = paneForm({ id: 'edit-batch-form', onSubmit: saveEdit, onCancel: closePane, submitLabel: 'Save changes', submitting: saving, children: <BatchFormFields form={form} setForm={setForm} instructors={instructors} isAdmin={isAdmin} /> });
    side = { open: true, onOpenChange: closePane, title: `Edit: ${batch?.name}`, icon: PencilSimple, children: f.body, footer: f.footer, testid: 'edit-batch-pane' };
  }

  const avgProgress = students.length ? Math.round(students.reduce((n, s) => n + (s.progress || 0), 0) / students.length) : 0;

  return (
    <DashboardLayout>
      <RecordPage
        title={batch?.name || 'Batch'}
        subtitle={batch ? [batch.code, batch.instructor_name ? `Instructor: ${batch.instructor_name}` : null, batch.start_date ? `${new Date(batch.start_date).toLocaleDateString()} → ${batch.end_date ? new Date(batch.end_date).toLocaleDateString() : '…'}` : null].filter(Boolean).join(' · ') || 'Batch' : 'Loading…'}
        badge={batch && <StatusBadge value={batch.status} />}
        onBack={() => navigate(base)}
        onRefresh={load}
        refreshing={loading}
        loading={loading && !batch}
        side={side}
        maxWidth="none"
        primary={[
          { key: 'add', label: 'Add students', icon: UserPlus, onClick: () => openPane('add-students'), testid: 'add-students-btn' },
          { key: 'courses', label: 'Assign courses', icon: Books, variant: 'outline', onClick: () => openPane('assign-courses'), testid: 'assign-courses-btn' },
        ]}
        secondary={[
          { key: 'edit', label: 'Edit batch', icon: PencilSimple, onClick: () => openPane('edit') },
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: deleteBatch },
        ]}
      >
        {batch && (
          <>
            <StatTiles items={[
              { label: 'Students', value: students.length, icon: Student },
              { label: 'Courses', value: batch.course_count || 0, icon: Books, iconClass: 'bg-violet-100 text-violet-700' },
              { label: 'Average progress', value: `${avgProgress}%`, icon: ChartBar, iconClass: 'bg-emerald-100 text-emerald-700' },
              { label: 'Status', value: <StatusBadge value={batch.status} />, icon: UsersThree, iconClass: 'bg-sky-100 text-sky-700' },
            ]} />
            {batch.description && <p className="text-[13px] text-muted-foreground -mt-1">{batch.description}</p>}

            <Tabs value={tab} onValueChange={setTab} className="space-y-3">
              <TabsList>
                <TabsTrigger value="students">Students ({students.length})</TabsTrigger>
                <TabsTrigger value="courses">Courses ({batch.courses?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="students">
                <DataGrid
                  title="Students"
                  rows={students}
                  loading={loading}
                  onRefresh={load}
                  searchKeys={['first_name', 'last_name', 'email', 'phone']}
                  columns={[
                    { key: 'name', header: 'Student', accessor: (s) => `${s.first_name} ${s.last_name}`, render: (s) => (
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-[11px] font-semibold">{`${s.first_name?.[0] || ''}${s.last_name?.[0] || ''}`.toUpperCase()}</div>
                        <span className="font-medium">{s.first_name} {s.last_name}</span>
                      </div>
                    ) },
                    { key: 'contact', header: 'Email / Phone', accessor: (s) => s.email || s.phone || '', className: 'text-muted-foreground' },
                    { key: 'student_level', header: 'Level', width: 110, accessor: (s) => s.student_level || '', className: 'capitalize', render: (s) => s.student_level || '—' },
                    { key: 'enrolled_courses', header: 'Enrolled', width: 100, align: 'right', render: (s) => `${s.enrolled_courses}/${batch.course_count || 0}` },
                    { key: 'progress', header: 'Progress', width: 150, render: (s) => (
                      <div className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, s.progress || 0)}%` }} /></div><span className="text-xs w-9 text-right">{Math.round(s.progress || 0)}%</span></div>
                    ) },
                    { key: 'is_active', header: 'Status', width: 100, accessor: (s) => s.is_active !== false, render: (s) => <StatusBadge value={s.is_active !== false} /> },
                  ]}
                  rowTone={(s) => (s.is_active === false ? 'muted' : null)}
                  selectionActions={[
                    { key: 'assign', label: 'Assign courses', icon: BookOpen, requires: 'any', onClick: (rows) => { setPane(null); assign.open(rows); } },
                    { key: 'view', label: 'View', icon: Eye, onClick: ([s]) => navigate(`${isAdmin ? '/admin' : '/instructor'}/users/${s.id}`), hidden: true },
                    { key: 'remove', label: 'Remove from batch', icon: Trash, variant: 'destructive', onClick: ([s]) => removeStudent(s) },
                  ]}
                  emptyMessage="No students yet — use Add students"
                />
              </TabsContent>

              <TabsContent value="courses">
                <DataGrid
                  title="Courses"
                  rows={batch.courses || []}
                  loading={loading}
                  onRefresh={load}
                  searchKeys={['title', 'course_code']}
                  columns={[
                    { key: 'title', header: 'Course', render: (c) => <span className="font-medium">{c.title}</span> },
                    { key: 'course_code', header: 'Code', width: 120, className: 'font-mono text-xs', render: (c) => c.course_code || '—' },
                    { key: 'is_published', header: 'Status', width: 120, accessor: (c) => (c.is_published ? 'published' : 'draft'), render: (c) => <StatusBadge value={c.is_published ? 'published' : 'draft'} /> },
                  ]}
                  onRowDoubleClick={(c) => navigate(`/instructor/courses/${c.id}`)}
                  selectionActions={[
                    { key: 'open', label: 'Open course', icon: Eye, onClick: ([c]) => navigate(`/instructor/courses/${c.id}`) },
                    { key: 'remove', label: 'Remove from batch', icon: Trash, variant: 'destructive', onClick: ([c]) => unassignCourse(c) },
                  ]}
                  emptyMessage="No courses assigned — use Assign courses"
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </RecordPage>
    </DashboardLayout>
  );
}
